import { paths } from "@classdojo/ts-api-types";
import { getApiClient } from "src/utils/axios";
import { makeOperation } from "src/modules/reactQuery";
import { AxiosResponse } from "axios";
import debounce from "lodash/fp/debounce";

// api/storyBatchAction?withStudentCommentsAndLikes=true
type UseStoryBatchActionRequestBody =
  paths["/api/storyBatchAction"]["post"]["requestBody"]["content"]["application/json"];

export const useStoryBatchActionOperation = makeOperation({
  mutationKey: "storyBatchActionOperation",
  mutationFn: async (body: UseStoryBatchActionRequestBody) => {
    if (
      body.actions.some(
        (action) => action.action === "like" || action.action === "unlike"
      )
    ) {
      // if there are any like or unlike actions, send them immediately
      return getApiClient().post(
        "/api/storyBatchAction?withStudentCommentsAndLikes=true",
        body,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    } else {
      // otherwise, put them in a batch to be sent
      return deferredStoryBatchAction(body);
    }
  },
});

let currentPromise: Promise<AxiosResponse<any>> | undefined = undefined;
let currentResolve: (() => void) | undefined = undefined;
let currentBatch: UseStoryBatchActionRequestBody["actions"] = [];
const BATCHING_INTERVAL = process.env.NODE_ENV === "test" ? 0 : 1000;

function deferredStoryBatchAction(body: UseStoryBatchActionRequestBody) {
  currentBatch = [...currentBatch, ...body.actions];

  if (!currentPromise) {
    currentPromise = new Promise<void>((resolve) => {
      currentResolve = resolve;
    }).then(() => {
      const batch = currentBatch;
      currentBatch = [];
      currentPromise = undefined;
      currentResolve = undefined;

      return getApiClient().post(
        "/api/storyBatchAction?withStudentCommentsAndLikes=true",
        { actions: batch },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    });
  }

  requestFlush();
  return currentPromise;
}

const requestFlush = debounce(BATCHING_INTERVAL)(() => {
  if (!currentResolve) {
    throw new Error(
      "currentResolve in queueFlush (storyBatchAction) should not be empty"
    );
  }
  currentResolve();
});
